import React, { createContext, useCallback, useContext } from 'react'

import useGlobalTheme, { broadcastThemeChange } from '../../hooks/useGlobalTheme'

const STORAGE_KEY = 'loadboard:theme'

const TOKENS = {
	light: {
		pageBg: 'bg-white',
		pageText: 'text-gray-900',
		navBg: 'bg-white border-b border-gray-200',
		border: 'border-gray-200',
		fieldShell:
			'relative rounded-md bg-white outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-sky-500 px-3 py-1.5',
		
		slimFieldShell:
			'relative rounded-md bg-white outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-sky-500 px-2.5 py-1',
		floatingLabel:
			'absolute -top-1.5 left-2.5 z-[1] inline-block bg-white px-1 text-[10px] font-semibold tracking-normal capitalize text-gray-600',
		inputText: 'text-gray-900',
		placeholder: 'placeholder-gray-400',
		optionBg: 'bg-white',
		swapBtn: 'text-gray-700 hover:text-gray-900 hover:bg-gray-200',
		tabActive: 'text-gray-900 border border-solid border-sky-400',
		tabInactive:
			'text-gray-400 border border-dotted border-gray-300 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400',
		tabSubtitle: 'text-gray-500',
		addTabBtn: 'bg-sky-500 text-white hover:bg-sky-600',
		closeTab: 'text-gray-400 hover:text-gray-700 hover:bg-gray-100',
		countBadge: 'bg-gray-200 text-gray-700',
		rowHover: 'hover:bg-gray-50',
		rowBorder: 'border-gray-100',
		valueStrong: 'text-gray-900',
		valueDim: 'text-gray-700',
		valueMuted: 'text-gray-500',
		rateMain: 'text-blue-800',
		sortBtn: 'text-gray-900 hover:bg-gray-200',
		dropdownBg: 'bg-white ring-1 ring-gray-200 shadow-lg',
		dropdownItem: 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
		dropdownDivider: 'border-gray-200',
	},
	dark: {
		pageBg: 'bg-[#15202b]',
		pageText: 'text-gray-200',
		navBg: 'bg-[#15202b]',
		border: 'border-white/[0.06]',
		fieldShell:
			'relative rounded-md bg-white/5 outline outline-1 -outline-offset-1 outline-white/10 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-sky-500 px-3 py-1.5',
		slimFieldShell:
			'relative rounded-md bg-white/5 outline outline-1 -outline-offset-1 outline-white/10 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-sky-500 px-2.5 py-1',
		floatingLabel:
			'absolute -top-1.5 left-2.5 z-[1] inline-block bg-[#15202b] px-1 text-[10px] font-semibold tracking-normal capitalize text-gray-300',
		inputText: 'text-gray-100',
		placeholder: 'placeholder-gray-500',
		optionBg: 'bg-[#15202b]',
		swapBtn: 'text-gray-400 hover:text-white hover:bg-white/[0.06]',
		tabActive: 'text-white border border-solid border-sky-400',
		tabInactive:
			'text-gray-500 border border-dotted border-white/20 hover:bg-white/[0.05] hover:text-gray-200 hover:border-white/40',
		tabSubtitle: 'text-gray-500',
		addTabBtn: 'bg-sky-500 text-white hover:bg-sky-400',
		closeTab: 'text-gray-500 hover:text-gray-200 hover:bg-white/10',
		countBadge: 'bg-white/10 text-gray-300',
		rowHover: 'hover:bg-white/[0.03]',
		rowBorder: 'border-white/[0.04]',
		valueStrong: 'text-gray-100',
		valueDim: 'text-gray-300',
		valueMuted: 'text-gray-400',
		rateMain: 'text-blue-400',
		sortBtn: 'text-gray-200 hover:bg-white/[0.08]',
		dropdownBg: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_0_10px_rgba(59,130,246,0.1)]',
		dropdownItem: 'text-gray-300 hover:bg-[#1c2834] hover:text-sky-400',
		dropdownDivider: 'border-gray-600',
	},
}

const ThemeContext = createContext({
	theme: 'dark',
	tokens: TOKENS.dark,
	toggle: () => {},
})

export function LoadboardThemeProvider({ children }) {
	const theme = useGlobalTheme()

	const toggle = useCallback(() => {
		const next = theme === 'light' ? 'dark' : 'light'
		try {
			localStorage.setItem(STORAGE_KEY, next)
		} catch {
			// localStorage can fail in private mode / over quota — non-fatal.
		}
		broadcastThemeChange(next)
	}, [theme])

	return (
		<ThemeContext.Provider value={{ theme, tokens: TOKENS[theme], toggle }}>
			{children}
		</ThemeContext.Provider>
	)
}

export const useLoadboardTheme = () => useContext(ThemeContext)
